/**
 * DeviceBodyGhostModel — simplified, transparent preview of the device body.
 *
 * Reads dimensions from the external preview store (during slider drag)
 * or from Redux (during worker update after commit). This avoids Redux
 * dispatches during drag, keeping undo history clean.
 *
 * Geometry is rebuilt when any dimension, cornerRadius, or edgeBevel changes.
 * Uses actual dimensions (not a reference cube) so the bevel is uniform
 * in world space, matching the real CSG body shape.
 */
import {useMemo, useEffect, useRef, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {createRoundedRectShape} from '../../../utils/geometry'
import {useAppSelector} from '../../../store'
import {selectActiveScene} from '../../store/scenesSlice'
import {getPreviewSnapshot, subscribePreview} from '../previewDimensions'
import {GHOST_COLOR, GHOST_BODY_OPACITY} from './ghostStyle'

/** Slight size increase to avoid z-fighting with the real body mesh */
const SIZE_OFFSET = 0.5

const DeviceBodyGhostModel = () => {
  // Preview store dimensions (during drag) take priority over Redux
  const preview = useSyncExternalStore(subscribePreview, getPreviewSnapshot)

  const reduxWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const reduxHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const reduxDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const reduxCornerRadius = useAppSelector((s) => selectActiveScene(s).deviceCornerRadius)
  const reduxEdgeBevel = useAppSelector((s) => selectActiveScene(s).deviceEdgeBevel)

  const width = (preview?.width ?? reduxWidth) + SIZE_OFFSET
  const height = (preview?.height ?? reduxHeight) + SIZE_OFFSET
  const depth = (preview?.depth ?? reduxDepth) + SIZE_OFFSET
  const cornerRadius = preview?.cornerRadius ?? reduxCornerRadius
  const edgeBevel = preview?.edgeBevel ?? reduxEdgeBevel

  const prevGeoRef = useRef<THREE.BufferGeometry | null>(null)

  // Build geometry using actual dimensions — same formula as createBodyExtruded
  const geometry = useMemo(() => {
    const clampedBevel = Math.min(edgeBevel, width / 2, height / 2, depth / 2)
    const innerW = Math.max(0.01, width - 2 * clampedBevel)
    const innerH = Math.max(0.01, height - 2 * clampedBevel)
    const innerD = Math.max(0.01, depth - 2 * clampedBevel)
    const clampedCorner = Math.min(cornerRadius, innerW / 2, innerH / 2)

    const shape = createRoundedRectShape(innerW, innerH, clampedCorner)
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: innerD,
      bevelEnabled: clampedBevel > 0,
      bevelThickness: clampedBevel,
      bevelSize: clampedBevel,
      bevelSegments: 16,
      curveSegments: 32,
    })
    geo.translate(0, 0, -(innerD / 2))
    return geo
  }, [width, height, depth, cornerRadius, edgeBevel])

  // Dispose previous geometry on change and on unmount
  useEffect(() => {
    if (prevGeoRef.current && prevGeoRef.current !== geometry) {
      prevGeoRef.current.dispose()
    }
    prevGeoRef.current = geometry
    return () => {
      geometry.dispose()
    }
  }, [geometry])

  return (
    <mesh geometry={geometry}>
      <meshBasicMaterial color={GHOST_COLOR} transparent opacity={GHOST_BODY_OPACITY} depthWrite={false} />
    </mesh>
  )
}

export default DeviceBodyGhostModel
